import { IRowHookOutputInternal } from 'dromo-uploader-js'
import {
  IRowCell,
  IRowHookInput,
  IRowToAdd,
} from 'dromo-uploader-js/dist/interfaces'
import {
  ProposedSite,
  ProposedSiteAction,
  ExistingSiteRecordMatch,
  SiteActionAPIResponse,
  SiteListType,
} from '@black-bear-energy/black-bear-energy-common'
import { SiteAction } from 'common/types'
import { existingRecordSiteActionOptions } from '../DromoUploader/commonFieldSchemas'
import { setSiteAction } from '../DromoUploader/setSiteAction'
import { AxiosInstance } from 'axios'

export async function generateSiteActions({
  records,
  isUpdate,
  siteListType,
  clientId,
  api,
}: {
  records: IRowHookInput[]
  isUpdate: boolean
  siteListType: SiteListType
  clientId: number
  api: AxiosInstance
}): Promise<{
  updatedRows: IRowHookOutputInternal[]
  newRows: IRowToAdd[]
  siteActionResponse: ProposedSiteAction[]
}> {
  if (records.length === 0) {
    return { updatedRows: [], newRows: [], siteActionResponse: [] }
  }

  const recordIdToIndexMap: Map<string, number> = new Map<string, number>()

  const proposedSites: ProposedSite[] = records.map((record) => {
    recordIdToIndexMap.set(record.rowId, record.index)
    return {
      id: record.rowId,
      clientDesignation: record.row.clientDesignation?.value as
        | string
        | undefined,
      name: record.row.name.value as string,
      streetAddress: record.row.address.value as string | undefined,
      state: record.row.state.value as string | undefined,
      latitude: record.row.latitude.value
        ? parseFloat(String(record.row.latitude.value))
        : undefined,
      longitude: record.row.longitude.value
        ? parseFloat(String(record.row.longitude.value))
        : undefined,
      buildingArea: record.row.buildingArea?.value
        ? parseFloat(String(record.row.buildingArea.value))
        : undefined,
    } satisfies ProposedSite
  })

  const siteActionResponse = (
    await api.post<ProposedSiteAction<{ [key: string]: string | number }>[]>(
      '/sites/generate-actions',
      {
        sites: proposedSites,
        clientId,
        siteListType,
      }
    )
  ).data

  const rowFields = Object.keys(records[0].row)

  const updatedRows: IRowHookOutputInternal[] = []
  const newRows: IRowToAdd[] = []
  const potentialMatchMap: Map<number, number | undefined> = new Map<
    number,
    number | undefined
  >()

  for (const siteAction of siteActionResponse) {
    if (
      siteListType !== SiteListType.Sold &&
      siteAction.action === SiteActionAPIResponse.Sold
    ) {
      // create a new row and populate with the existing db record's data
      newRows.push(
        generateSoldRecordRow(
          siteAction,
          rowFields,
          potentialMatchMap.get(siteAction.record.id as number)
        )
      )
      continue
    }
    // If this site has a potential match, save the index of this record so we
    // can insert the potential match below this row
    if (
      siteAction.action === SiteActionAPIResponse.Review &&
      siteAction.potentialMatch
    ) {
      potentialMatchMap.set(
        siteAction.potentialMatch.id,
        recordIdToIndexMap.get(siteAction.id)
      )
    }
    // apply to the existing row
    updatedRows.push(
      setSiteAction({
        siteListType,
        siteAction,
        record: records.find((r) => r.rowId === siteAction.id) as IRowHookInput,
        isUpdate,
        getMatchingDBRecordDecription,
      })
    )
  }
  return {
    updatedRows,
    newRows,
    siteActionResponse: siteActionResponse as ProposedSiteAction[],
  }
}

function generateSoldRecordRow(
  siteAction: ProposedSiteAction<{ [key: string]: string | number }>,
  rowFieldNames: string[],
  index?: number
): IRowToAdd {
  if (siteAction.action !== SiteActionAPIResponse.Sold) {
    throw new Error('generateSoldRecordRow() called for invalid site action')
  }

  const rowEntries = rowFieldNames.map((rowFieldName) => {
    const cell: IRowCell = {
      value: siteAction.record[rowFieldName],
      info: [{ message: 'Potentially sold', level: 'warning' }],
    }
    const rowEntry: [string, IRowCell] = [rowFieldName, cell]
    return rowEntry
  })
  const row = Object.fromEntries(rowEntries)

  row.siteAction.value = SiteAction.Review
  row.siteAction.selectOptions = existingRecordSiteActionOptions
  // set the id of the existing record
  row.existingRecordId.value = siteAction.record.id
  // mark this as a potentially sold record added from the db
  row.maybeSold.value = true
  // if we have the index of a potential match, insert this new row after it
  return { row, index: index === undefined ? undefined : index + 1 }
}

function getMatchingDBRecordDecription(match: ExistingSiteRecordMatch): string {
  const clientDes = match.clientDesignation
    ? `<br>Client Designation: "${match.clientDesignation}"`
    : ''
  const buildingArea = match.buildingArea
    ? `<br>Building Area: ${match.buildingArea} sqft`
    : ''
  return `Name: "${match.name}"${clientDes}<br>Address: ${match.fullAddress}${buildingArea}`
}
